import { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import U2000ActionBtn from '../../u2000Core/components/buttons/U2000ActionBtn';
import { NS } from '../U3400';
import { useU3400Context } from '../U3400Context';
import type { U3400TileLogicalUnitCode } from '../U3400Service';
import { useU3400DashboardHandlers } from '../hooks/useU3400DashboardHandlers';
import { u3400MoveTileInDraft, u3400ReorderTiles } from '../utils/u3400LayoutTiles';
import U3400EmbeddedTile from './U3400EmbeddedTile';
import U3400TileManagerDialog from './U3400TileManagerDialog';

function U3400DashboardScreen() {
  const { t } = useTranslation(NS);
  const { initLU, layout } = useU3400Context();
  const { updateLayout } = useU3400DashboardHandlers();
  const [managerOpen, setManagerOpen] = useState(false);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [reordering, setReordering] = useState(false);
  const dragFromIndexRef = useRef<number | null>(null);

  const catalogueEmpty = initLU.catalogueTiles.length === 0;
  const tiles = layout.tiles ?? [];

  const persistTiles = async (nextTiles: U3400TileLogicalUnitCode[]) => {
    if (reordering) return;
    setReordering(true);
    try {
      await updateLayout(nextTiles, { optimistic: true });
    } finally {
      setReordering(false);
    }
  };

  const handleDragStart = (index: number) => (event: DragEvent) => {
    dragFromIndexRef.current = index;
    setDraggingIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (index: number) => (event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dropTargetIndex !== index) {
      setDropTargetIndex(index);
    }
  };

  const handleDragEnd = () => {
    dragFromIndexRef.current = null;
    setDraggingIndex(null);
    setDropTargetIndex(null);
  };

  const handleDrop = (toIndex: number) => (event: DragEvent) => {
    event.preventDefault();
    const fromIndex = dragFromIndexRef.current;
    handleDragEnd();
    if (fromIndex == null || fromIndex === toIndex) {
      return;
    }
    const nextTiles = u3400ReorderTiles(tiles, fromIndex, toIndex);
    if (nextTiles === tiles) {
      return;
    }
    void persistTiles(nextTiles);
  };

  const handleKeyboardMove = (index: number, direction: 'up' | 'down') => {
    const nextTiles = u3400MoveTileInDraft(tiles, index, direction);
    if (nextTiles === tiles) {
      return;
    }
    void persistTiles(nextTiles);
  };

  const openManager = () => setManagerOpen(true);

  return (
    <div className="flex flex-col gap-4 p-4">
      {catalogueEmpty ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {t('catalogue.empty')}
        </div>
      ) : (
        <div className="flex items-center justify-end">
          <U2000ActionBtn
            label={t('manager.open')}
            variant="secondary"
            handleClick={openManager}
          />
        </div>
      )}

      {tiles.length === 0 ? (
        <div className="flex flex-col items-center gap-4 rounded-md border border-dashed border-gray-300 bg-white px-4 py-12 text-center">
          <div>
            <p className="text-sm font-medium text-gray-800">{t('slot.emptyTitle')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t('slot.emptyBody')}</p>
          </div>
          {!catalogueEmpty && (
            <U2000ActionBtn
              label={t('manager.openEmpty')}
              variant="primary"
              handleClick={openManager}
            />
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {tiles.map((code: U3400TileLogicalUnitCode, index: number) => (
            <div
              key={`${code}-${index}`}
              onDragOver={handleDragOver(index)}
              onDrop={handleDrop(index)}
              className={`group relative flex flex-col gap-3 rounded-md transition-opacity ${
                draggingIndex === index ? 'opacity-60' : ''
              } ${
                dropTargetIndex === index && draggingIndex !== index
                  ? 'ring-2 ring-blue-400 ring-offset-2'
                  : ''
              }`}
            >
              <div className="flex items-center gap-1 self-end">
                <button
                  type="button"
                  className="inline-flex h-7 min-w-7 items-center justify-center rounded-sm border border-gray-200 bg-white px-1.5 text-xs text-gray-600 shadow-xs transition-opacity hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-100 disabled:text-gray-300 pointer-events-none opacity-0 max-lg:pointer-events-auto max-lg:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100"
                  disabled={index === 0 || reordering}
                  onClick={() => handleKeyboardMove(index, 'up')}
                  title={t('manager.moveUp')}
                  aria-label={t('manager.moveUp')}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="inline-flex h-7 min-w-7 items-center justify-center rounded-sm border border-gray-200 bg-white px-1.5 text-xs text-gray-600 shadow-xs transition-opacity hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-100 disabled:text-gray-300 pointer-events-none opacity-0 max-lg:pointer-events-auto max-lg:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100"
                  disabled={index === tiles.length - 1 || reordering}
                  onClick={() => handleKeyboardMove(index, 'down')}
                  title={t('manager.moveDown')}
                  aria-label={t('manager.moveDown')}
                >
                  ↓
                </button>
                <span
                  role="button"
                  tabIndex={reordering ? -1 : 0}
                  draggable={!reordering}
                  onDragStart={handleDragStart(index)}
                  onDragEnd={handleDragEnd}
                  className="inline-flex h-7 w-7 cursor-grab items-center justify-center rounded-sm border border-gray-200 bg-white text-gray-500 shadow-xs hover:bg-gray-50 hover:text-gray-700 active:cursor-grabbing focus:outline-hidden focus:ring-2 focus:ring-blue-500/40"
                  title={t('manager.dragHint')}
                  aria-label={t('manager.dragHint')}
                >
                  <span aria-hidden="true" className="select-none text-sm leading-none">
                    ⋮⋮
                  </span>
                </span>
              </div>
              <U3400EmbeddedTile code={code} />
            </div>
          ))}
        </div>
      )}

      {!catalogueEmpty && (
        <U3400TileManagerDialog
          isOpen={managerOpen}
          onClose={() => setManagerOpen(false)}
        />
      )}
    </div>
  );
}

export default U3400DashboardScreen;
